import React, { useState } from 'react';
import { Send, Edit, Trash2, X, Plus, Mail, CheckSquare, Square } from 'lucide-react';

import axios, { API } from './apiClient';

// Auto-attach Bearer token for cross-origin auth
axios.interceptors.request.use(config => {
  const token = sessionStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const EmailTab = ({ leads, prospects, customers, emailLogs, emailTemplates, onSave }) => {
  const [activeEmailTab, setActiveEmailTab] = useState('compose');
  const [selectedType, setSelectedType] = useState('leads');
  const [selectedIds, setSelectedIds] = useState([]);
  const [emailForm, setEmailForm] = useState({ subject: '', body: '' });
  const [templateForm, setTemplateForm] = useState({ name: '', subject: '', body: '' });
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const allEntities = selectedType === 'leads' ? leads : selectedType === 'prospects' ? prospects : customers;

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const selectAll = () => {
    if (selectedIds.length === allEntities.length) setSelectedIds([]);
    else setSelectedIds(allEntities.map(e => e.id));
  };

  const handleSendBulk = async () => {
    if (selectedIds.length === 0) { alert('Please select at least one recipient'); return; }
    if (!emailForm.subject || !emailForm.body) { alert('Please fill in subject and body'); return; }
    
    setIsSending(true);
    setSendResult(null);
    try {
      const res = await axios.post(`${API}/admin/email/bulk`, {
        entity_type: selectedType,
        entity_ids: selectedIds,
        subject: emailForm.subject,
        body: emailForm.body,
        is_html: true
      }, {});
      setSendResult(res.data);
      setSelectedIds([]);
      onSave();
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error(e);
      setSendResult({ message: 'Error sending emails', results: [] });
    } finally { setIsSending(false); }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.body) { alert('Please fill all fields'); return; }
    try {
      await axios.post(`${API}/admin/email/templates`, templateForm, {});
      setTemplateForm({ name: '', subject: '', body: '' });
      onSave();
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error(e); }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await axios.delete(`${API}/admin/email/templates/${id}`, {});
    onSave();
  };

  const loadTemplate = (template) => {
    setEmailForm({ subject: template.subject, body: template.body });
    setActiveEmailTab('compose');
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Rajdhani, sans-serif' }}>Email Center</h2>
      
      {/* Sub-tabs */}
      <div className="flex border-b mb-6">
        {['compose', 'templates', 'logs'].map(tab => (
          <button key={tab} onClick={() => setActiveEmailTab(tab)} className={`px-6 py-3 font-medium capitalize ${activeEmailTab === tab ? 'border-b-2 border-orange-500 text-orange-600' : 'text-slate-500'}`}>{tab === 'compose' ? 'Bulk Email' : tab}</button>
        ))}
      </div>

      {/* Compose/Bulk Email */}
      {activeEmailTab === 'compose' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left: Select Recipients */}
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Select Recipients</h3>
            <div className="flex space-x-2 mb-4">
              {['leads', 'prospects', 'customers'].map(type => (
                <button key={type} onClick={() => { setSelectedType(type); setSelectedIds([]); }} className={`px-4 py-2 rounded capitalize ${selectedType === type ? 'bg-orange-500 text-white' : 'bg-slate-100'}`}>{type}</button>
              ))}
            </div>
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm text-slate-500">{selectedIds.length} of {allEntities.length} selected</span>
              <button onClick={selectAll} className="text-sm text-blue-600 hover:underline">{selectedIds.length === allEntities.length ? 'Deselect All' : 'Select All'}</button>
            </div>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {allEntities.map(e => (
                <div key={e.id} onClick={() => toggleSelect(e.id)} className={`p-3 rounded cursor-pointer flex items-center ${selectedIds.includes(e.id) ? 'bg-orange-50 border-2 border-orange-500' : 'bg-slate-50 border-2 border-transparent'}`}>
                  {selectedIds.includes(e.id) ? <CheckSquare size={18} className="text-orange-500 mr-3" /> : <Square size={18} className="text-slate-400 mr-3" />}
                  <div>
                    <p className="font-medium">{e.company_name}</p>
                    <p className="text-sm text-slate-500">{e.email}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Email Form */}
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Compose Email</h3>
            <p className="text-sm text-slate-500 mb-4">Use <code className="bg-slate-100 px-1">{'{{first_name}}'}</code>, <code className="bg-slate-100 px-1">{'{{last_name}}'}</code>, <code className="bg-slate-100 px-1">{'{{company_name}}'}</code> for personalization.</p>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium mb-1">Subject *</label><input type="text" value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} className="w-full px-3 py-2 border rounded" placeholder="Email subject..." /></div>
              <div><label className="block text-sm font-medium mb-1">Body *</label><textarea value={emailForm.body} onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })} className="w-full px-3 py-2 border rounded font-mono text-sm" rows="10" placeholder="<p>Hello {{first_name}},</p>..."></textarea></div>
              <button onClick={handleSendBulk} disabled={isSending || selectedIds.length === 0} className={`w-full py-3 rounded text-white font-medium ${isSending || selectedIds.length === 0 ? 'bg-slate-400' : 'bg-orange-500 hover:bg-orange-600'}`}>{isSending ? 'Sending...' : `Send to ${selectedIds.length} Recipients`}</button>
            </div>
            {sendResult && (
              <div className={`mt-4 p-4 rounded ${sendResult.results?.some(r => !r.success) ? 'bg-yellow-50' : 'bg-green-50'}`}>
                <p className="font-medium">{sendResult.message}</p>
                {sendResult.results?.filter(r => !r.success).length > 0 && (
                  <p className="text-sm text-red-600 mt-1">{sendResult.results.filter(r => !r.success).length} emails failed</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Templates */}
      {activeEmailTab === 'templates' && (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Create Template</h3>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium mb-1">Template Name</label><input type="text" value={templateForm.name} onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })} className="w-full px-3 py-2 border rounded" /></div>
              <div><label className="block text-sm font-medium mb-1">Subject</label><input type="text" value={templateForm.subject} onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })} className="w-full px-3 py-2 border rounded" /></div>
              <div><label className="block text-sm font-medium mb-1">Body</label><textarea value={templateForm.body} onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })} className="w-full px-3 py-2 border rounded" rows="6"></textarea></div>
              <button onClick={handleSaveTemplate} className="w-full py-2 bg-blue-500 text-white rounded">Save Template</button>
            </div>
          </div>
          <div className="bg-white border rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Saved Templates</h3>
            <div className="space-y-3">
              {emailTemplates.map(t => (
                <div key={t.id} className="p-4 bg-slate-50 rounded">
                  <div className="flex justify-between items-start">
                    <div><p className="font-semibold">{t.name}</p><p className="text-sm text-slate-500">{t.subject}</p></div>
                    <div className="flex space-x-2">
                      <button onClick={() => loadTemplate(t)} className="text-blue-500 text-sm">Use</button>
                      <button onClick={() => handleDeleteTemplate(t.id)} className="text-red-500"><Trash2 size={16} /></button>
                    </div>
                  </div>
                </div>
              ))}
              {emailTemplates.length === 0 && <p className="text-center text-slate-500 py-4">No templates yet.</p>}
            </div>
          </div>
        </div>
      )}

      {/* Logs */}
      {activeEmailTab === 'logs' && (
        <div className="bg-white border rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Email History</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b"><th className="text-left py-2">Date</th><th className="text-left py-2">To</th><th className="text-left py-2">Subject</th><th className="text-center py-2">Status</th></tr></thead>
              <tbody>
                {emailLogs.map(log => (
                  <tr key={log.id} className="border-b">
                    <td className="py-2">{new Date(log.sent_at).toLocaleString()}</td>
                    <td className="py-2">{log.to_email}</td>
                    <td className="py-2">{log.subject}</td>
                    <td className="py-2 text-center"><span className={`px-2 py-1 rounded text-xs ${log.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{log.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {emailLogs.length === 0 && <p className="text-center text-slate-500 py-8">No emails sent yet.</p>}
          </div>
        </div>
      )}
    </div>
  );
};


export default EmailTab;
